/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';

import InlineEditableTextfield from '@atlaskit/inline-edit/inline-editable-textfield';
import type { InlineEditableTextfieldProps } from '@atlaskit/inline-edit/types';
import { token } from '@atlaskit/tokens';

export interface SubheaderInlineEditProps extends Omit<InlineEditableTextfieldProps, 'isCompact'> {}

/**
 * Overrides the default `@atlaskit/inline-edit` styles so that both the read view and edit view
 * match the appearance of the `PanelSubheader` title (`Heading`, `size="medium"`).
 *
 * `@atlaskit/inline-edit` does not expose a styling hook for its read view text or the underlying
 * textfield input, so the typography is overridden here by targeting the rendered elements. The
 * whole control is also shifted start-ward so the title text lines up with the rest of the panel
 * content, with the hover highlight and edit-view textfield bleeding into the subheader's padding.
 */
const containerStyles = css({
	minWidth: 0,
	flexGrow: 1,
	marginBlock: token('space.negative.025'),
	marginInlineStart: token('space.negative.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& form > div': {
		marginBlockStart: token('space.0'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& [data-compact]': {
		font: token('font.heading.medium'),
		paddingBlock: token('space.0'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& input': {
		font: token('font.heading.medium'),
		marginInlineStart: token('border.width'),
		paddingBlock: token('space.0'),
	},
});

/**
 * __Subheader inline edit__
 *
 * A wrapper around `@atlaskit/inline-edit`'s `InlineEditableTextfield`, restyled to match the
 * `PanelSubheader` title. Pass it to the `inlineEdit` prop of `PanelSubheader` to make the subheader
 * title editable in place.
 */
export function SubheaderInlineEdit({
	readViewFitContainerWidth = true,
	analyticsContext,
	cancelButtonLabel,
	confirmButtonLabel,
	defaultValue,
	label,
	validate,
	keepEditViewOpenOnBlur,
	hideActionButtons,
	isRequired,
	editButtonLabel,
	editLabel,
	onCancel,
	startWithEditViewOpen,
	onConfirm,
	placeholder,
	testId,
}: SubheaderInlineEditProps): JSX.Element {
	return (
		<div css={containerStyles}>
			<InlineEditableTextfield
				analyticsContext={analyticsContext}
				cancelButtonLabel={cancelButtonLabel}
				confirmButtonLabel={confirmButtonLabel}
				defaultValue={defaultValue}
				label={label}
				validate={validate}
				keepEditViewOpenOnBlur={keepEditViewOpenOnBlur}
				hideActionButtons={hideActionButtons}
				isRequired={isRequired}
				editButtonLabel={editButtonLabel}
				editLabel={editLabel}
				onCancel={onCancel}
				startWithEditViewOpen={startWithEditViewOpen}
				onConfirm={onConfirm}
				placeholder={placeholder}
				testId={testId}
				readViewFitContainerWidth={readViewFitContainerWidth}
				isCompact
			/>
		</div>
	);
}
